import { ImageUtils } from '../../utils/image-utils';
import { StringUtils } from '../../utils/string-utils';
import { Meme } from './meme-generator';
import { Dimension, Rectangle } from './meme.types';

export const MEME_IMAGE = 'MemeImage';
export const MEME_STORED_IMAGE = 'MemeStoredImage';
export const MEME_SPACE_IMAGE = 'MemeSpaceImage';

export class ImageHelper {
    static loadImage(imageUrl: string): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = (event) => {
                resolve(img);
            };
            img.onerror = () => {
                reject();
            };
            img.src = imageUrl;
        });
    }
}

export interface Border {
    top: number;
    bottom: number;
}

export class MemeImage {
    public static TYPE: string = MEME_IMAGE;

    private meme: Meme;
    private id: string;
    private srcUrl: string;

    private loaded = false;
    private image: HTMLImageElement;
    private boundingRectangle: Rectangle;

    private border: Border = { top: 0, bottom: 0 };
    private bgColor = 'black';

    constructor(imageSrc: string, meme: Meme) {
        this.meme = meme;
        this.srcUrl = imageSrc;
        this.id = 'x-' + StringUtils.uuid();
    }

    async loadImage() {
        this.image = await ImageHelper.loadImage(this.srcUrl);
        this.loaded = true;
        this.updateBoundingRectangle();
    }

    isLoaded() {
        return this.loaded;
    }

    getImage() {
        return this.image;
    }

    async setImage(imageSrc: string) {
        this.loaded = false;
        this.srcUrl = imageSrc;
        await this.loadImage();
        this.updateBoundingRectangle();
    }

    protected calculateWidthAndHeightFor(width: number): Dimension {
        if (!this.loaded) {
            throw new Error('Image source is not loaded. Src: ' + this.srcUrl);
        }
        const imageWidth = this.image.naturalWidth;
        const imageHeight = this.image.naturalHeight;
        return {
            width: width,
            height: imageHeight * (width / imageWidth),
        };
    }

    public updateBoundingRectangle() {
        let offsetY = 0;
        for (const image of this.meme.images) {
            if (image !== this) {
                offsetY += image.calculateWidthAndHeightFor(this.meme.getMemeWidth()).height + (image.border.top + image.border.bottom);
            } else {
                break;
            }
        }
        const dim = this.calculateWidthAndHeightFor(this.meme.getMemeWidth());
        this.boundingRectangle = {
            x: 0,
            y: offsetY,
            width: dim.width,
            height: dim.height + (this.border.bottom + this.border.top),
        };
    }

    public getBorder() {
        return this.border;
    }

    public setBorder(border: Border) {
        this.border = border;
    }

    public setBorderTop(top: number) {
        this.border.top = top;
    }
    public setBorderBottom(bottom: number) {
        this.border.bottom = bottom;
    }

    public getBackgroundColor(): string {
        return this.bgColor;
    }

    public setBackgroundColor(bgColor: string): void {
        this.bgColor = bgColor;
    }

    public getBoundingRectangle(): Rectangle {
        return this.boundingRectangle;
    }

    public getImageBoundingRectangle(): Rectangle {
        return {
            x: this.boundingRectangle.x,
            y: this.boundingRectangle.y + this.border.top,
            width: this.boundingRectangle.width,
            height: this.boundingRectangle.height - (this.border.top + this.border.bottom),
        };
    }
}

export class MemeSpaceImage extends MemeImage {
    public static TYPE: string = MEME_SPACE_IMAGE;

    public constructor(meme: Meme, dimention: Dimension, color: string) {
        super(ImageUtils.createImageSource(dimention, color), meme);
    }
}

export class MemeStoredImage extends MemeImage {
    public static TYPE: string = MEME_STORED_IMAGE;

    constructor(imageSrc: string, meme: Meme) {
        super(imageSrc, meme);
    }
}
